import React, { useCallback, useEffect, useState } from "react";
import { Building2, Users, Loader2, ArrowLeft, Crown } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

const PERMISSIONS = ["owner", "admin", "recruiter", "hiring_manager", "interviewer"];
const PERM_LABEL = {
    owner: "Owner", admin: "Admin", recruiter: "Recruiter",
    hiring_manager: "Hiring Manager", interviewer: "Interviewer",
};

const PermPill = ({ perm }) => {
    const tones = {
        owner: "bg-emerald-100 text-emerald-800",
        admin: "bg-sky-100 text-sky-800",
        recruiter: "bg-violet-100 text-violet-800",
        hiring_manager: "bg-amber-100 text-amber-900",
        interviewer: "bg-slate-100 text-slate-700",
    };
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tones[perm] || tones.interviewer}`}>
            {perm === "owner" && <Crown className="h-3 w-3" />}
            {PERM_LABEL[perm] || perm}
        </span>
    );
};

export default function EmployerOrgs() {
    const [orgs, setOrgs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);      // employer_id
    const [detail, setDetail] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const loadOrgs = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/employer-orgs");
            setOrgs(r.data.orgs || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadDetail = useCallback(async (employerId) => {
        setDetail(null);
        try {
            const r = await api.get(`/admin/employer-orgs/${employerId}`);
            setDetail(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }, []);

    useEffect(() => { loadOrgs(); }, [loadOrgs]);

    const openOrg = (org) => {
        setSelected(org.employer_id);
        loadDetail(org.employer_id);
    };

    const changePerm = async (member, newPerm) => {
        if (newPerm === member.permission) return;
        setBusyId(member.user_id);
        try {
            await api.patch(
                `/admin/employer-orgs/${selected}/members/${member.user_id}/permission`,
                { permission: newPerm }
            );
            toast.success(`Updated ${member.email} to ${PERM_LABEL[newPerm]}`);
            await loadDetail(selected);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    };

    if (selected && detail) {
        return (
            <div data-testid="admin-employer-org-detail">
                <button
                    onClick={() => { setSelected(null); setDetail(null); }}
                    className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline"
                    data-testid="admin-employer-org-back"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> All employer orgs
                </button>
                <h1 className="mt-3 font-heading font-medium text-2xl tracking-tight text-foreground">
                    Employer org — {detail.member_count} member{detail.member_count === 1 ? "" : "s"}
                </h1>
                <p className="text-sm text-muted-foreground mt-1 font-mono">{detail.employer_id}</p>

                <div className="mt-6 rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="admin-employer-org-members-table">
                        <thead>
                            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                <th className="text-left px-4 py-2">Member</th>
                                <th className="text-left px-4 py-2">Permission</th>
                                <th className="text-left px-4 py-2">Auth</th>
                                <th className="text-left px-4 py-2">Status</th>
                                <th className="text-left px-4 py-2">Joined</th>
                            </tr>
                        </thead>
                        <tbody>
                            {detail.members.map((m) => (
                                <tr key={m.user_id} className="border-b border-border last:border-0" data-testid={`admin-org-member-${m.user_id}`}>
                                    <td className="px-4 py-3">
                                        <div className="font-medium text-foreground">{m.name || m.email}</div>
                                        <div className="text-xs text-muted-foreground">{m.email}</div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <select
                                            value={m.permission}
                                            onChange={(e) => changePerm(m, e.target.value)}
                                            disabled={busyId === m.user_id}
                                            data-testid={`admin-org-perm-select-${m.user_id}`}
                                            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                                        >
                                            {PERMISSIONS.map((p) => (
                                                <option key={p} value={p}>{PERM_LABEL[p]}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground">{m.auth_provider || "—"}</td>
                                    <td className="px-4 py-3 text-xs">{m.status}</td>
                                    <td className="px-4 py-3 text-xs text-muted-foreground">
                                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="admin-employer-orgs">
            <p className="overline">Command Center</p>
            <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                Employer Orgs
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
                Every employer organization, their Owner, and their team size.
            </p>

            <div className="mt-6">
                {loading ? (
                    <div className="py-16 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-emerald-500" /></div>
                ) : orgs.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
                        No employer orgs yet.
                    </div>
                ) : (
                    <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
                        <table className="w-full text-sm" data-testid="admin-employer-orgs-table">
                            <thead>
                                <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                    <th className="text-left px-4 py-2">Org</th>
                                    <th className="text-left px-4 py-2">Owner</th>
                                    <th className="text-left px-4 py-2">Members</th>
                                    <th className="text-left px-4 py-2">Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orgs.map((o) => (
                                    <tr
                                        key={o.employer_id}
                                        onClick={() => openOrg(o)}
                                        data-testid={`admin-org-row-${o.employer_id}`}
                                        className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/40"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                <span className="font-medium text-foreground">{o.company_name || "—"}</span>
                                            </div>
                                            <div className="mt-0.5 text-[11px] font-mono text-muted-foreground">{o.employer_id}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="text-foreground">{o.owner_name || "—"}</div>
                                            <div className="text-xs text-muted-foreground">{o.owner_email || "—"}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-800">
                                                <Users className="h-3 w-3" /> {o.member_count}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                            {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
